import React from 'react';

/**
 * StatusBadge — Origin-style pill for contract status.
 *
 * The actual visual lives in components.css as `.chip` + `.status-<value>`.
 * Kept as a component so existing callers (`<StatusBadge status={x} />`)
 * keep working without knowing about the class layering.
 *
 * Props:
 *   status:   'draft' | 'in_review' | 'active' | 'expired' | 'terminated'
 *   size:     'small' | 'default' | 'large'  (maps to chip sq / chip / chip lg)
 *   showDot:  render the leading colored dot (default true)
 */

const LABELS = {
  draft: 'Draft',
  in_review: 'In Review',
  active: 'Active',
  expired: 'Expired',
  terminated: 'Terminated',
};

export default function StatusBadge({ status, size = 'default', showDot = true }) {
  const key = status && LABELS[status] ? status : 'draft';
  const label = LABELS[key];
  const chipClass = size === 'small' ? 'chip sq' : 'chip';
  return (
    <span className={`${chipClass} status-${key}`}>
      {showDot && size !== 'small' && <span className="dot" />}
      {label}
    </span>
  );
}

const DOT_BG = {
  draft: 'var(--color-gray-400)',
  in_review: 'var(--color-yellow-500)',
  active: 'var(--color-green-500)',
  expired: 'var(--color-red-500)',
  terminated: 'var(--color-gray-400)',
};

/** StatusDot — just the colored dot, no pill, for dense rows. */
export function StatusDot({ status }) {
  const key = status && LABELS[status] ? status : 'draft';
  return (
    <span
      style={{
        display: 'inline-block', width: 8, height: 8, borderRadius: 999,
        background: DOT_BG[key],
      }}
      aria-label={LABELS[key]}
    />
  );
}
